import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { AdSense } from '../components/AdSense';

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');

  // Load reCAPTCHA script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    // Define callback for reCAPTCHA
    window.onRecaptchaSuccess = (token) => {
      setRecaptchaToken(token);
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      setError('All fields are required');
      return;
    }

    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to send message');
        setLoading(false);
        return;
      }

      setSuccess('✅ Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setRecaptchaToken('');
      
      // Reset reCAPTCHA
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div
        className="page-header bg-gradient text-white py-5"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          minHeight: '250px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="container">
          <h1 className="display-5 fw-bold">Contact Us</h1>
          <p className="lead">We'd love to hear from you</p>
        </div>
      </div>

      <div className="container py-5">
        <div className="row g-5">
          <div className="col-12 col-lg-6">
            <h2 className="mb-4">Get in Touch</h2>
            
            {error && <div className="alert alert-danger mb-4">{error}</div>}
            {success && <div className="alert alert-success mb-4">{success}</div>}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="name" className="form-label">
                  Your Name
                </label>
                <input 
                  type="text" 
                  className="form-control" 
                  id="name" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required 
                  disabled={loading}
                  autoComplete="name"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input 
                  type="email" 
                  className="form-control" 
                  id="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required 
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="subject" className="form-label">
                  Subject
                </label>
                <input 
                  type="text" 
                  className="form-control" 
                  id="subject" 
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required 
                  disabled={loading}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="message" className="form-label">
                  Message
                </label>
                <textarea
                  className="form-control"
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                ></textarea>
              </div>

              {/* reCAPTCHA */}
              <div className="mb-4">
                <div 
                  className="g-recaptcha" 
                  data-sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                  data-callback="onRecaptchaSuccess"
                ></div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-lg"
                disabled={loading || !recaptchaToken}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          <div className="col-12 col-lg-6">
            <h2 className="mb-4">Contact Information</h2>

            <div className="mb-4">
              <h5 className="mb-3">📍 Address</h5>
              <p>
                Hybrid Events Inc.
                <br />
                Alo@laba Festival S.A
              </p>
            </div>

            <div className="mb-4">
              <h5 className="mb-3">📞 Phone</h5>
              <p>
                <a href="tel:+1234567890">+1 (849) 249-3627</a>
              </p>
            </div>

            <div className="mb-4">
              <h5 className="mb-3">📧 Email</h5>
              <p>
                <a href="mailto:info@alolaba.makeitservices.org">info@alolaba.makeitservices.org</a>
                <br />
                <a href="mailto:support@makeitservices.org">support@makeitservices.org</a>
              </p>
            </div>

            <div className="mb-4">
              <h5 className="mb-3">🕒 Business Hours</h5>
              <p>
                Monday - Friday: 9:00 AM - 6:00 PM
                <br />
                Saturday: 10:00 AM - 4:00 PM
                <br />
                Sunday: Closed
              </p>
            </div>

            <div>
              <h5 className="mb-3">Follow Us</h5>
              <div className="d-flex gap-3">
                <a href="#" className="btn btn-outline-primary btn-sm">
                  Facebook
                </a>
                <a href="#" className="btn btn-outline-primary btn-sm">
                  Twitter
                </a>
                <a href="#" className="btn btn-outline-primary btn-sm">
                  Instagram
                </a>
                <a href="#" className="btn btn-outline-primary btn-sm">
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdSense />

      <Footer />
    </div>
  );
}

export default ContactPage;
